// v10/src/components/VoiceSelector.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Volume2, VolumeX, CheckCircle2, Sparkles } from 'lucide-react';
import { VOICE_MODES, getVoiceMode, setVoiceMode } from '../utils/speechHelper';

const VoiceSelector = ({ aircraft, darkMode, onClose, orgFeatures = {} }) => {
  const [selectedMode,    setSelectedMode]    = useState(getVoiceMode());
  const handleModeChange = (mode) => {
    setSelectedMode(mode);
    setVoiceMode(mode);
  };

  const elevenlabsEnabled = orgFeatures.advanced_voice !== false;

  useEffect(() => {
    if (!elevenlabsEnabled && selectedMode === VOICE_MODES.ELEVENLABS) {
      handleModeChange(VOICE_MODES.DISABLED);
    }
  }, [elevenlabsEnabled]);

  const text  = darkMode ? 'white'                 : '#0f172a';
  const sub   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const cardBg = darkMode ? 'rgba(255,255,255,0.04)' : 'white';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';

  return (
    <motion.div
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{ position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center',
        justifyContent:'center', padding:16, background:'rgba(0,0,0,0.6)',
        backdropFilter:'blur(6px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale:0.92, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.92, y:20 }}
        transition={{ type:'spring', duration:0.35 }}
        onClick={e => e.stopPropagation()}
        style={{ width:'100%', maxWidth:480, borderRadius:24, overflow:'hidden',
          background: darkMode?'#1e293b':'white',
          boxShadow:'0 24px 60px rgba(0,0,0,0.3)' }}
      >
        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:border, display:'flex',
          alignItems:'center', gap:12 }}>
          <div style={{ width:40, height:40, borderRadius:12, background:'rgba(37,99,235,0.1)',
            display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Volume2 style={{ width:20, height:20, color:'#2563eb' }} />
          </div>
          <div>
            <p style={{ margin:0, fontSize:16, fontWeight:800, color:text }}>
              Configuración de Voz
            </p>
            <p style={{ margin:0, fontSize:12, color:sub }}>
              Elegí cómo querés escuchar el asistente
            </p>
          </div>
        </div>

        <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:16,
          maxHeight:'65vh', overflowY:'auto' }}>

          {/* ── Modo: Con voz IA ── */}
          {elevenlabsEnabled && (
            <div>
              <motion.button
                onClick={() => handleModeChange(VOICE_MODES.ELEVENLABS)}
                whileTap={{ scale:0.98 }}
                style={{ width:'100%', padding:'16px', borderRadius:16, cursor:'pointer',
                  border: selectedMode === VOICE_MODES.ELEVENLABS
                    ? '2px solid #7c3aed' : border,
                  background: selectedMode === VOICE_MODES.ELEVENLABS
                    ? (darkMode?'rgba(124,58,237,0.12)':'rgba(124,58,237,0.05)')
                    : cardBg,
                  textAlign:'left', fontFamily:'inherit' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:40, height:40, borderRadius:12, flexShrink:0,
                    background: selectedMode===VOICE_MODES.ELEVENLABS
                      ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : (darkMode?'rgba(255,255,255,0.06)':'#f1f5f9'),
                    display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Sparkles style={{ width:20, height:20,
                      color: selectedMode===VOICE_MODES.ELEVENLABS?'white':'#64748b' }} />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <p style={{ margin:0, fontSize:14, fontWeight:700, color:text }}>
                        Voz IA — ElevenLabs
                      </p>
                      {selectedMode === VOICE_MODES.ELEVENLABS && (
                        <CheckCircle2 style={{ width:16, height:16, color:'#16a34a' }} />
                      )}
                      <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px',
                        borderRadius:20, background:'rgba(124,58,237,0.1)', color:'#7c3aed' }}>
                        ✨ IA
                      </span>
                    </div>
                    <p style={{ margin:'2px 0 0', fontSize:12, color:sub }}>
                      Voz natural generada por inteligencia artificial
                    </p>
                  </div>
                </div>
              </motion.button>

            </div>
          )}

          {/* ── Modo: Sin voz ── */}
          <motion.button
            onClick={() => handleModeChange(VOICE_MODES.DISABLED)}
            whileTap={{ scale:0.98 }}
            style={{ width:'100%', padding:'16px', borderRadius:16, cursor:'pointer',
              border: selectedMode === VOICE_MODES.DISABLED ? '2px solid #64748b' : border,
              background: selectedMode === VOICE_MODES.DISABLED
                ? (darkMode?'rgba(100,116,139,0.12)':'rgba(100,116,139,0.05)')
                : cardBg,
              textAlign:'left', fontFamily:'inherit' }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:40, height:40, borderRadius:12, flexShrink:0,
                background: selectedMode===VOICE_MODES.DISABLED
                  ? '#64748b' : (darkMode?'rgba(255,255,255,0.06)':'#f1f5f9'),
                display:'flex', alignItems:'center', justifyContent:'center' }}>
                <VolumeX style={{ width:20, height:20,
                  color: selectedMode===VOICE_MODES.DISABLED?'white':'#64748b' }} />
              </div>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <p style={{ margin:0, fontSize:14, fontWeight:700, color:text }}>Sin Voz</p>
                  {selectedMode === VOICE_MODES.DISABLED && (
                    <CheckCircle2 style={{ width:16, height:16, color:'#16a34a' }} />
                  )}
                </div>
                <p style={{ margin:'2px 0 0', fontSize:12, color:sub }}>
                  Checklist solo visual · Silencioso · Ahorra batería
                </p>
              </div>
            </div>
          </motion.button>

          {/* Aviso plan sin IA */}
          {!elevenlabsEnabled && (
            <div style={{ padding:'12px 16px', borderRadius:12,
              background:'rgba(124,58,237,0.07)', border:'1px solid rgba(124,58,237,0.2)',
              display:'flex', alignItems:'center', gap:10 }}>
              <Sparkles style={{ width:14, height:14, color:'#7c3aed', flexShrink:0 }} />
              <p style={{ margin:0, fontSize:12, color:'#7c3aed', fontWeight:600 }}>
                La voz IA está disponible en el plan Pro.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 24px', borderTop:border }}>
          <motion.button onClick={onClose}
            whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
            style={{ width:'100%', padding:'13px', borderRadius:14, border:'none',
              cursor:'pointer', background:'linear-gradient(135deg,#2563eb,#7c3aed)',
              color:'white', fontWeight:700, fontSize:14, fontFamily:'inherit' }}>
            Guardar y continuar
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default VoiceSelector;
